import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const fallbackPrivacyPolicy = `**LENDS MARKETPLACE**  
**PRIVACY POLICY**  
**Last Updated: \[5/8/2026\]**

This Privacy Policy describes how Lends LLC (“Lends,” “we,” “us,” or “our”) collects, uses, discloses, and protects information about you when you access or use our website, mobile applications, and related services (collectively, the “Platform”). This Privacy Policy applies to all users of the Platform, including individuals who create accounts, list or offer goods, spaces, or services, or book or otherwise use such offerings through the Platform. By accessing or using the Platform, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with this Privacy Policy, you should not access or use the Platform. Capitalized terms not otherwise defined herein have the meanings set forth in the Terms of Service.

1. Information We Collect. We collect information about you from a variety of sources, including information you provide directly, information collected automatically when you use the Platform, and information from third parties.  
     
   1. Information You Provide to Us. We collect information that you provide directly when you create an account, use the Platform, or communicate with us or other Users. This may include:  
        
      1. Account Information: such as your name, email address, phone number, username, and password  
      2. Profile Information: such as profile photos, business information, descriptions, and preferences  
      3. Listing Information: details about Goods, Spaces, or Services you offer, including descriptions, pricing, availability, images, and specifications  
      4. Booking Information: details you provide when making or managing a Booking.  
      5. Communications: messages you send through the Platform (including communications between Providers and Customers)  
      6. Support Requests: information you provide when contacting customer support

   

   2. Transaction and Payment Information. When you make or receive payments through the Platform, we collect information related to those transactions, including:  
        
      1. Booking details and transaction history   
      2. Billing information (such as name and billing address)   
      3. Payment status and related records

   

   Payment processing is handled by third-party Payment Processors, and we do not store full payment card details. Such information is collected and processed in accordance with the Payment Processor’s privacy policy.

   

   3. Identity Verification Information. We may collect information to verify your identity or prevent fraud, including:  
        
      1. Government-issued identification  
      2. Date of birth  
      3. Photographs or images submitted for identity verification purposes

   

   This information may be collected directly or through third-party verification providers.

   

   4. Information Collected Automatically. When you access or use the Platform, we automatically collect certain information, including:  
        
      1. Device Information: such as IP address, browser type, operating system, and device identifiers  
      2. Usage Information: such as pages viewed, features used, search queries, and interactions with the Platform  
      3. Log Data: timestamps, referring/exit pages, and clickstream data

   

   5. Cookies and Tracking Technologies. We use cookies, pixels, and similar tracking technologies to collect information about your interactions with the Platform and to improve your experience. These technologies may be used to remember your preferences, analyze usage, and support marketing efforts. You can manage cookie preferences through your browser settings.

   

   6. Location Information. Location information may be used to facilitate Listings, Bookings, logistics, fraud prevention, and to improve the Platform. We may collect information about your location, including:  
        
      1. Approximate location based on IP address  
      2. More precise location data (if you enable location services)

   

   7. Information from Third Parties. We may receive information about you from third parties. This information may be combined with other information we collect about you. Such third parties may include:  
        
      1. Payment processors  
      2. Identity verification providers  
      3. Analytics providers  
      4. Business partners or referral sources

2. How We Use Information. We use the information we collect for a variety of business and operational purposes, including to provide, maintain, improve, and protect the Platform and our Users.   
     
   1. To Ensure Safety or Prevent Fraud. We may preserve, review, and disclose information where reasonably necessary to investigate disputes, enforce our agreements, prevent fraud, or protect the safety of Users or the Platform. We may disclose information where we believe disclosure is necessary to prevent imminent harm, respond to emergencies, or protect the safety of any person.

   

   2. To Provide and Operate the Platform. We use information to:  
        
      1. Create and manage user accounts;  
      2. Facilitate Listings and Bookings;  
      3. Process transactions and payments;  
      4. Enable communications between Providers and Customers; and  
      5. Provide customer support and respond to inquiries.

   

   3. To Personalize and Improve the Platform. We use information to:  
        
      1. Personalize user experiences and content;  
      2. Analyze usage trends and user activity;  
      3. Improve the functionality, performance, and security of the Platform; and  
      4. Develop new features, products, and services.

   

   4. To Communicate with You. We use information to send you:  
        
      1. Transactional and operational communications, including confirmations, updates, reminders, and support messages;  
      2. Notices relating to your account, Bookings, or the Platform; and  
      3. Marketing or promotional communications, where permitted by law.

   

   You may opt out of certain promotional communications by following the instructions provided in those communications.

   

   5. To Promote Safety and Security. We use information to:  
        
      1. Verify identities and account information;  
      2. Detect, investigate, and prevent fraud, abuse, unauthorized activity, and other harmful conduct;  
      3. Enforce our Terms of Service and other policies; and  
      4. Protect the rights, property, safety, and security of the Platform, our Users, and third parties.

   

   We may review, monitor, or retain communications made through the Platform for purposes including customer support, fraud prevention, safety, enforcement of our Terms, and legal compliance.

   

   6. To Comply with Legal Obligations. We may use information to comply with applicable laws, regulations, legal processes, governmental requests, and industry standards, including tax, accounting, reporting, and compliance obligations.

   

   7. For Analytics and Advertising. We may use information to analyze trends, measure usage of the Platform, and improve our marketing and advertising efforts. We and our third-party partners may use cookies and similar technologies to deliver content and advertisements that may be relevant to your interests, subject to applicable law.

   

   8. Aggregated and De-Identified Information. We may aggregate, anonymize, or de-identify information so that it cannot reasonably be used to identify you. We may use such aggregated or de-identified information for any lawful business purpose, including analytics, research, and improvement of the Platform.

3. How We Share Information. We may share information about you as described in this Privacy Policy or as otherwise disclosed to you at the time of collection.  
     
   1. Sharing Between Users. In order to facilitate Listings and Bookings, we may share certain information between Providers and Customers, including:  
        
      1. Names and profile information;   
      2. Contact information;   
      3. Booking details; and   
      4. Communications relating to Listings or Bookings. 

   

   Users may use such information only for purposes reasonably related to the applicable Booking or use of the Platform. Users may not use information obtained through the Platform for unsolicited marketing, harassment, unlawful purposes, or to circumvent the Platform or its payment platforms.

   

   2. Service Providers and Vendors. We may share information with third-party service providers, contractors, and vendors that perform services on our behalf, including providers of:  
        
      1. Payment processing;  
      2. Cloud hosting and storage;  
      3. Analytics and marketing services;  
      4. Customer support;  
      5. Identity verification and fraud prevention; and  
      6. Communications and messaging tools.

   These parties are authorized to use information only as necessary to provide services to us or as otherwise permitted by applicable law.

   

   3. Payment Processors. Payments made through the Platform are processed by third-party Payment Processors. We may share transaction and account information with such Payment Processors to facilitate payments, prevent fraud, and comply with legal obligations.

   

   4. Legal Compliance and Protection. We may disclose information if we believe such disclosure is necessary or appropriate to:  
        
      1. Comply with applicable law, regulation, legal process, or governmental request;  
      2. Enforce our Terms of Service or other agreements;  
      3. Investigate or prevent fraud, security issues, or illegal activity; or  
      4. Protect the rights, property, safety, or security of Lends, our Users, or others.

   

   5. Business Transfers. We may share or transfer information in connection with a merger, acquisition, financing, reorganization, sale of assets, bankruptcy, or other corporate transaction involving all or part of our business.

   

   6. With Your Consent or Direction. We may share information with third parties when you direct us to do so or otherwise consent to such sharing.

   

   7. Aggregated and De-Identified Information. We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you for lawful business purposes, including analytics, research, and marketing.

   

   8. Public Content. Certain information may be publicly visible through the Platform, including profile information, Listings, reviews, ratings, and other User Content that you choose to make publicly available.

4. Cookies and Tracking Technologies. We and our third-party service providers may use cookies, pixels, web beacons, software development kits (“SDKs”), and similar tracking technologies (“Tracking Technologies”) to collect information about your use of the Platform and to improve your experience.  
     
   1. Types of Tracking Technologies We Use. We may use the following categories of Tracking Technologies:  
        
      1. Essential Technologies: necessary for the operation of the Platform, authentication, security, and core functionality;  
      2. Functional Technologies: used to remember preferences and settings;  
      3. Analytics Technologies: used to understand how Users interact with the Platform and improve functionality and performance; and  
      4. Advertising and Marketing Technologies: used to deliver and measure relevant advertisements and marketing communications, where applicable.

   

   2. Information Collected Through Tracking Technologies. Tracking Technologies may collect information such as:  
        
      1. IP address;  
      2. Browser type and device information;  
      3. Pages viewed and interactions with the Platform;  
      4. Referral URLs;  
      5. Timestamps and session information; and  
      6. Approximate location information.

   

   3. Purposes of Tracking Technologies. We may use Tracking Technologies to:  
        
      1. Operate and secure the Platform;  
      2. Remember user preferences and settings;  
      3. Analyze traffic and usage trends;  
      4. Personalize content and user experiences;  
      5. Improve the Platform and our services; and  
      6. Support marketing and advertising activities.

   

   4. Third-Party Analytics and Advertising. We may use third-party analytics and advertising providers that use Tracking Technologies to collect information about your interactions with the Platform and other websites or services over time. These third parties may use the information they collect in accordance with their own privacy policies.

   

   5. Managing Cookies and Preferences. Most web browsers allow you to control cookies through browser settings. You may choose to disable or remove cookies; however, doing so may affect the functionality or availability of certain features of the Platform. Depending on your jurisdiction, you may also have additional choices regarding the use of certain Tracking Technologies.

   

   6. Do Not Track Signals. Some web browsers may transmit “Do Not Track” signals. Because there is no universally accepted standard for how to respond to such signals, the Platform may not respond to “Do Not Track” requests.

5. Data Retention and Security. We retain information for as long as reasonably necessary to provide the Platform, comply with legal obligations, resolve disputes, enforce agreements, and protect our business and Users.  
     
   1. Data Retention. Retention periods may vary depending on the nature of the information and the purposes for which it was collected. We may retain information:  
        
      1. For the duration of your account or relationship with the Platform;  
      2. As necessary to complete transactions and fulfill Bookings;  
      3. To comply with legal, tax, accounting, and regulatory obligations;  
      4. To resolve disputes and enforce our agreements; and  
      5. To detect, prevent, or investigate fraud, security incidents, or unlawful activity.

   

   2. Account Deletion. You may request deletion of your account and certain personal information by contacting us or through available account settings. Upon receiving such a request, we may delete or anonymize certain information, subject to applicable law and our legitimate business needs. We may retain certain information following account deletion where necessary to:  
        
      1. Comply with legal obligations;  
      2. Complete ongoing transactions or Bookings;  
      3. Resolve disputes;  
      4. Enforce our agreements; or  
      5. Protect against fraud, abuse, or legal claims.

   

   3. Data Security. We implement reasonable administrative, technical, and organizational safeguards designed to protect information against unauthorized access, loss, misuse, disclosure, alteration, or destruction. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security of your information.

   

   4. User Responsibility. You are responsible for maintaining the confidentiality of your account credentials and for using appropriate security measures when accessing the Platform. If you believe your account or information has been compromised, you should notify us immediately.

   

   5. Third-Party Services. Certain information may be processed or stored by third-party vendors and service providers, including Payment Processors, cloud service providers, analytics vendors, and identity verification providers. While we take reasonable steps to work with reputable providers, we are not responsible for the security practices of third parties.

6. User Rights and Choices. Depending on your location and applicable law, you may have certain rights and choices regarding your personal information.  
     
   1. Access and Update of Information. You may access, update, or correct certain account information at any time through your account settings or by contacting us. You are responsible for ensuring that your information remains accurate and current.

   

   2. Account Deletion. You may request deletion of your account and certain personal information as described in Section 5 (Data Retention and Security). Please note that we may retain certain information as required or permitted by law and for legitimate business purposes.

   

   3. Marketing Communications. You may opt out of receiving promotional emails from us by following the unsubscribe instructions included in such communications. Even if you opt out of promotional communications, we may still send you transactional or administrative messages relating to your account, Bookings, or use of the Platform.

   

   4. Cookies and Tracking Choices. Most web browsers allow you to control cookies through browser settings. Depending on your jurisdiction, you may also have additional choices regarding the use of certain Tracking Technologies, including analytics or advertising cookies.

   

   5. California Privacy Rights. If you are a California resident, you may have certain rights under applicable California privacy laws, including the right to:  
        
      1. Request information regarding the categories of personal information we collect, use, disclose, or share;  
      2. Request deletion of certain personal information;  
      3. Request correction of inaccurate personal information; and  
      4. Opt out of certain uses or disclosures of personal information, where applicable.

   

   We will not discriminate against you for exercising any privacy rights available under applicable law. California residents may submit requests by contacting us using the information provided below.

   

   6. Additional Jurisdiction-Specific Rights. Depending on your location, you may have additional rights under applicable privacy laws, including rights relating to access, portability, objection to processing, restriction of processing, or withdrawal of consent. We will respond to requests in accordance with applicable law.

   

   7. Verification of Requests. To protect your privacy and security, we may take reasonable steps to verify your identity before responding to certain requests relating to your personal information.

7. Children’s Privacy. The Platform is not intended for individuals under the age of eighteen (18), and we do not knowingly collect personal information from children under eighteen. If we become aware that we have collected personal information from a child under eighteen without appropriate authorization or legal basis, we may delete such information. If you believe that a child has provided personal information through the Platform, please contact us using the contact information provided below.

8. Changes to This Privacy Policy. We may update or modify this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or the Platform. If we make material changes to this Privacy Policy, we may provide notice through the Platform, by email, or by other reasonable means prior to the changes becoming effective. The “Last Updated” date at the top of this Privacy Policy indicates when this Privacy Policy was most recently revised. Your continued access to or use of the Platform after any updated Privacy Policy becomes effective constitutes your acknowledgment of the revised Privacy Policy.

9. Contact Information. If you have any questions, requests, or concerns regarding this Privacy Policy or our privacy practices, you may contact us at:

\[Company Name\]  
\[Mailing Address\]  
\[Email Address\]  
\[Phone Number, if applicable\]

Privacy-related requests may also be submitted through the Platform where available.

10. Applicability. This Privacy Policy does not apply to the privacy or data handling practices of Providers, Customers, or other Users. Providers are solely responsible for complying with applicable laws relating to surveillance or recording devices in Spaces and for disclosing the existence of any such devices where required by law. The Platform is intended for Users located in the United States.

11. Third-Party Links and Services. The Platform may contain links to third-party websites, applications, or services that are not operated or controlled by Lends. This Privacy Policy does not apply to third-party services, and we are not responsible for the privacy practices or content of such third parties. We encourage you to review the privacy policies of any third-party services you access. 

`;

export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'privacy',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Privacy Policy' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackPrivacyPolicy,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Privacy Policy | Lends Marketplace',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Privacy Policy for Lends Marketplace.',
    },
  },
};

const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
